/**
 * @fileoverview Shared constants for the Resource Management domain.
 */

// =============================================================================
// RESOURCE CATEGORIES
// =============================================================================

/**
 * Canonical resource category values.
 * - inspiration: Reference material that inspired story elements
 * - canon: Official/authoritative content for the story
 * - misc: General resources without specific classification
 * - character-asset-external: Manually uploaded character images (portraits, references)
 * - character-asset-generated: AI-generated character portraits
 * - generated-art: AI-generated visual content from the visual prompt feature
 * - world-object-asset-external: Manually uploaded world object images
 * - world-object-asset-generated: AI-generated world object images
 */
export const RESOURCE_CATEGORIES = {
  INSPIRATION: 'inspiration',
  CANON: 'canon',
  MISC: 'misc',
  CHARACTER_ASSET_EXTERNAL: 'character-asset-external',
  CHARACTER_ASSET_GENERATED: 'character-asset-generated',
  GENERATED_ART: 'generated-art',
  WORLD_OBJECT_ASSET_EXTERNAL: 'world-object-asset-external',
  WORLD_OBJECT_ASSET_GENERATED: 'world-object-asset-generated',
} as const;

/** Display labels for resource categories */
export const RESOURCE_CATEGORY_LABELS: Record<string, string> = {
  [RESOURCE_CATEGORIES.INSPIRATION]: 'Inspiration',
  [RESOURCE_CATEGORIES.CANON]: 'Canon',
  [RESOURCE_CATEGORIES.MISC]: 'Miscellaneous',
  [RESOURCE_CATEGORIES.CHARACTER_ASSET_EXTERNAL]: 'Character Asset (External)',
  [RESOURCE_CATEGORIES.CHARACTER_ASSET_GENERATED]: 'Character Asset (Generated)',
  [RESOURCE_CATEGORIES.GENERATED_ART]: 'Generated Art',
  [RESOURCE_CATEGORIES.WORLD_OBJECT_ASSET_EXTERNAL]: 'World Object Asset (External)',
  [RESOURCE_CATEGORIES.WORLD_OBJECT_ASSET_GENERATED]: 'World Object Asset (Generated)',
};

/** Categories available for user selection in upload dropdowns */
export const USER_SELECTABLE_CATEGORIES = [
  RESOURCE_CATEGORIES.INSPIRATION,
  RESOURCE_CATEGORIES.CANON,
  RESOURCE_CATEGORIES.MISC,
  RESOURCE_CATEGORIES.CHARACTER_ASSET_EXTERNAL,
  RESOURCE_CATEGORIES.WORLD_OBJECT_ASSET_EXTERNAL,
] as const;

/** Categories that are only assigned programmatically by the system */
export const SYSTEM_ASSIGNED_CATEGORIES = [
  RESOURCE_CATEGORIES.CHARACTER_ASSET_GENERATED,
  RESOURCE_CATEGORIES.GENERATED_ART,
  RESOURCE_CATEGORIES.WORLD_OBJECT_ASSET_GENERATED,
] as const;

/** Default category for new uploads */
export const DEFAULT_RESOURCE_CATEGORY = RESOURCE_CATEGORIES.MISC;

// =============================================================================
// FILE UPLOAD CONSTRAINTS
// =============================================================================

export const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10MB

/**
 * Maximum content size for files used as AI agent input.
 * Files larger than this limit cannot be used for sequel/inspiration generation
 * as they would exceed the context window of the AI models.
 */
export const MAX_AGENT_INPUT_CONTENT_SIZE = 300_000; // ~300K characters

export const ALLOWED_TEXT_MIMETYPES = ['text/plain', 'text/html'];
export const ALLOWED_IMAGE_MIMETYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const EPUB_MIMETYPE = 'application/epub+zip';

// EPUBs are accepted on upload but converted to text/plain before storage,
// so they do not appear in ALLOWED_TEXT_MIMETYPES used by downstream readers.
export const ALLOWED_UPLOAD_MIMETYPES = [
  ...ALLOWED_TEXT_MIMETYPES,
  ...ALLOWED_IMAGE_MIMETYPES,
  EPUB_MIMETYPE,
];

// Some browsers/OSes report EPUBs with an empty or generic MIME type (e.g.
// application/octet-stream), so we also sniff by extension.
export function isEpubFile(file: { name: string; type: string }): boolean {
  return (
    file.type === EPUB_MIMETYPE ||
    file.name.toLowerCase().endsWith('.epub')
  );
}

export function isAllowedUpload(file: { name: string; type: string }): boolean {
  return ALLOWED_UPLOAD_MIMETYPES.includes(file.type) || isEpubFile(file);
}

export type ImageSize = 'thumbnail' | 'medium' | 'full';

/**
 * Image size variants for optimized delivery.
 * - thumbnail: For grids, lists, and small previews (200px)
 * - medium: For cards and medium displays (512px)
 * - full: For lightbox and detailed views (1024px)
 */
export const IMAGE_SIZES: Record<ImageSize, number> = {
  thumbnail: 200,
  medium: 512,
  full: 1024,
};